import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="bg-white rounded-xl shadow p-8 mb-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">{children}</div>
  </section>
);

export const PoliticaEditorialPage: React.FC = () => {
  const canonicalUrl = `${SITE_URL}/politica-editorial`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `Política Editorial | ${SITE_NAME}`,
    url: canonicalUrl,
    inLanguage: 'pt-BR',
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>Política Editorial | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Como o ${SITE_NAME} seleciona, produz, revisa e atualiza conteúdo sobre criptomoedas: critérios de publicação, uso de fontes, tratamento de rumores, uso de IA e identificação de conteúdo patrocinado.`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Política Editorial</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Política Editorial</h1>
          <p className="text-gray-500 mb-8">
            Como selecionamos, produzimos, revisamos e atualizamos o conteúdo do {SITE_NAME}.
            Última atualização: julho de 2026.
          </p>

          <Section title="Critérios de seleção">
            <p>
              O {SITE_NAME} monitora continuamente publicações internacionais reconhecidas do mercado de
              criptoativos — entre elas CoinDesk, CoinTelegraph, Bitcoin.com News e CryptoSlate. Uma notícia
              é selecionada quando atende a pelo menos um destes critérios:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>relevância direta para o mercado de criptoativos (preço, adoção, tecnologia, segurança);</li>
              <li>impacto regulatório ou institucional, no Brasil ou no exterior;</li>
              <li>interesse prático para o leitor brasileiro (impostos, exchanges, produtos disponíveis no país);</li>
              <li>valor educativo para quem está começando no setor.</li>
            </ul>
            <p>
              Conteúdo relacionado a apostas, cassinos, jogos de azar e promoções desse tipo é
              <strong> bloqueado automaticamente</strong> pelo nosso pipeline e não é publicado, mesmo quando
              aparece em fontes que acompanhamos.
            </p>
          </Section>

          <Section title="Critérios de publicação">
            <p>Antes de ir ao ar, todo conteúdo passa por um pipeline que verifica:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>existência de fonte identificável, com link para o material original;</li>
              <li>ausência de termos e temas vetados pela linha editorial;</li>
              <li>duplicidade com conteúdo já publicado;</li>
              <li>tradução e resumo em português com linguagem neutra e informativa.</li>
            </ul>
            <p>
              Não publicamos conteúdo com promessa de lucro, linguagem promocional disfarçada de notícia ou
              informação financeira que não possa ser verificada na fonte.
            </p>
          </Section>

          <Section title="Uso de fontes">
            <p>
              Todo artigo identifica a publicação de origem e mantém link para a matéria original. Priorizamos,
              nesta ordem: comunicados e documentos oficiais (empresas, reguladores, projetos), documentação
              técnica e repositórios oficiais, e cobertura de veículos jornalísticos reconhecidos do setor.
            </p>
            <p>
              O {SITE_NAME} não copia textos integrais de terceiros: publicamos resumos e traduções editoriais
              com atribuição clara, e recomendamos a leitura da fonte original.
            </p>
          </Section>

          <Section title="Tratamento de rumores">
            <p>
              Rumores e informações não confirmadas só são publicados quando têm relevância de mercado e, ainda
              assim, são sempre identificados como não confirmados no texto, com indicação de quem reportou
              originalmente. Não publicamos rumores sobre preço com potencial de induzir decisões de compra ou
              venda.
            </p>
          </Section>

          <Section title="Política de atualização">
            <p>
              Notícias em desenvolvimento podem ser atualizadas conforme novos fatos surgem. A data de
              publicação e a data da última atualização ficam visíveis em cada artigo. Alterações relevantes de
              conteúdo (não apenas de forma) são tratadas conforme a nossa{' '}
              <Link to="/politica-de-correcoes" className="text-blue-600 hover:underline">Política de Correções</Link>.
            </p>
          </Section>

          <Section title="Uso de inteligência artificial">
            <p>
              O {SITE_NAME} usa IA como ferramenta de apoio editorial, de forma transparente. Hoje, a IA é
              utilizada para:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>tradução</strong> de títulos e textos do inglês para o português;</li>
              <li><strong>resumos</strong> em português dos pontos principais de cada notícia;</li>
              <li><strong>classificação</strong> de temas e categorias.</li>
            </ul>
            <p>
              Os artigos indicam quando tradução e resumo foram produzidos com auxílio de IA. O conteúdo passa
              por validações automáticas (filtros editoriais, checagem de fonte) e por revisão humana por
              amostragem. A responsabilidade editorial final é sempre humana. Erros podem ser reportados pelo
              nosso <Link to="/contact" className="text-blue-600 hover:underline">canal de contato</Link>.
            </p>
          </Section>

          <Section title="Conflitos de interesse">
            <p>
              O {SITE_NAME} não recebe pagamento de projetos de criptoativos para cobertura noticiosa, não
              possui posição relevante em ativos específicos que influencie a linha editorial e não publica
              matérias encomendadas sem identificação. A receita do site vem de publicidade programática
              claramente identificada como anúncio.
            </p>
          </Section>

          <Section title="Conteúdo patrocinado">
            <p>
              Caso o site venha a publicar conteúdo patrocinado, ele será sempre identificado de forma
              explícita com rótulo próprio ("Conteúdo patrocinado" ou "Publieditorial"), separado da cobertura
              noticiosa. Anúncios programáticos são identificados como publicidade pela própria plataforma que
              os veicula.
            </p>
          </Section>

          <Section title="Opinião, notícia e conteúdo educativo">
            <p>Distinguimos três tipos de conteúdo:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Notícia</strong> — relato factual de eventos, sempre com fonte;</li>
              <li><strong>Análise/opinião</strong> — interpretação assinada, identificada como tal;</li>
              <li><strong>Conteúdo educativo</strong> — guias e glossário, sem caráter de recomendação.</li>
            </ul>
            <p>
              Nenhum conteúdo do {SITE_NAME}, de qualquer tipo, constitui recomendação de investimento.
              Criptoativos envolvem alto risco e volatilidade.
            </p>
          </Section>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-sm text-blue-900">
            Veja também:{' '}
            <Link to="/politica-de-correcoes" className="text-blue-700 font-semibold hover:underline">Política de Correções</Link>
            {' · '}
            <Link to="/sobre" className="text-blue-700 font-semibold hover:underline">Sobre o {SITE_NAME}</Link>
            {' · '}
            <Link to="/termos-de-uso" className="text-blue-700 font-semibold hover:underline">Termos de Uso</Link>
          </div>
        </div>
      </div>
    </>
  );
};
